/**
 * QuickAddPersonFieldset - one person on the "Quick Add User" modal.
 *
 * Atomic Design role: Molecule. Composes the Input atom, the SegmentedControl
 * and SearchableSelect molecules, and LinkButton into one card:
 *
 *   [ CSR | JSR | Full User ]          ← type, CSR by default
 *   First name*      Last name*
 *   Primary business (search to filter) ← defaults to the work order's business
 *   Email            Phone              ← optional for CSR/JSR, required for Full User
 *   Password                            ← optional for CSR/JSR, required for Full User
 *
 * A Full User also gets a short "what they get" note, since they are a real
 * sign-in account: the role they start with and the welcome email they will
 * receive.
 *
 * Controlled: the modal owns the draft and its errors; the rules live in
 * utils/quickAddUser.
 */
import React from 'react';
import { Input } from '../../atoms/Input';
import { Alert } from '../../atoms/Alert';
import { LinkButton } from '../../atoms/LinkButton';
import { SegmentedControl } from '../SegmentedControl';
import { SearchableSelect } from '../SearchableSelect';
import type { SearchableSelectOption } from '../SearchableSelect';
import { changeQuickAddType, quickAddType } from '../../../utils/quickAddUser';
import type { QuickAddDraft, QuickAddDraftErrors, QuickAddPersonType, QuickAddTypeConfig } from '../../../utils/quickAddUser';
import { generateTempPassword } from '../../../utils/tempPassword';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface QuickAddPersonFieldsetProps {
	/** The person being filled in. */
	draft: QuickAddDraft;
	/** Called with the whole updated draft on every change. */
	onChange: (draft: QuickAddDraft) => void;
	/** Field-keyed validation messages. */
	errors?: QuickAddDraftErrors;
	/** Error from the server for this person (e.g. "email already taken"). */
	submitError?: string | null;
	/** The person types this creator may use (already filtered by role). */
	typeOptions: QuickAddTypeConfig[];
	/** Businesses for the primary-business picker. */
	companyOptions: SearchableSelectOption[];
	/** True while the business list is loading. */
	companiesLoading?: boolean;
	/** 1-based position, for the "Person 2" heading. */
	position: number;
	/** Given, shows a Remove link (omitted when this is the only person). */
	onRemove?: () => void;
	/** Disables every field (while saving). */
	disabled?: boolean;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const QuickAddPersonFieldset: React.FC<QuickAddPersonFieldsetProps> = ({
	draft,
	onChange,
	errors = {},
	submitError = null,
	typeOptions,
	companyOptions,
	companiesLoading = false,
	position,
	onRemove,
	disabled = false,
}) => {
	const config = quickAddType(draft.type);
	const required = config.credentialsRequired;
	/* Ids must be unique per card — several people can be on the modal at once. */
	const id = (field: string) => `${draft.key}-${field}`;

	/** Patch one field of the draft. */
	const set = <K extends keyof QuickAddDraft>(field: K, value: QuickAddDraft[K]) => onChange({ ...draft, [field]: value });

	return (
		<fieldset className="quick-add-person-fieldset flex flex-col gap-12px p-16px rounded-lg border border-brand-border bg-white" data-ref="molecule-quick-add-person-fieldset" disabled={disabled}>
			{/* Names the group for screen readers. A <legend> refuses to sit in a
			    flex row, so the visible heading below is a plain span. */}
			<legend className="sr-only">{`Person ${position}`}</legend>

			{/* Heading row: which person this is, and a way to drop them. */}
			<div className="flex items-center justify-between gap-8px">
				<span className="text-14px font-sans font-semibold text-brand-text" aria-hidden="true">{`Person ${position}`}</span>
				{onRemove && (
					<LinkButton size="sm" onClick={onRemove} className="text-red-600 hover:text-red-800" aria-label={`Remove person ${position}`}>
						Remove
					</LinkButton>
				)}
			</div>

			{submitError && <Alert variant="error">{submitError}</Alert>}

			{/* Type — only rendered when there is a choice to make. A creator
			    allowed a single type (a manager: Full User only) just sees it named. */}
			{typeOptions.length > 1 ? (
				<SegmentedControl<QuickAddPersonType>
					label="Type"
					options={typeOptions.map((t) => ({ value: t.value, label: t.label, description: t.description }))}
					value={draft.type}
					onChange={(type) => onChange(changeQuickAddType(draft, type))}
					disabled={disabled}
				/>
			) : (
				<p className="text-13px font-sans text-brand-text-muted">
					Type: <span className="font-semibold text-brand-text">{config.label}</span>
				</p>
			)}
			<p className="-mt-4px text-12px font-sans text-brand-text-muted">{config.description}</p>

			{/* Name — two columns from tablet up. */}
			<div className="grid grid-cols-1 tablet:grid-cols-2 gap-12px">
				<Input id={id('first-name')} label="First name" required value={draft.firstName} onChange={(e) => set('firstName', e.target.value)} error={errors.firstName} autoFocus={position === 1} />
				<Input id={id('last-name')} label="Last name" required value={draft.lastName} onChange={(e) => set('lastName', e.target.value)} error={errors.lastName} />
			</div>

			{/* Primary business — searchable; pre-selected to the work order's
			    business by the modal, changeable here. */}
			<div className="w-full">
				<label htmlFor={id('company')} className="block text-14px font-sans font-medium text-gray-700 mb-4px">
					Primary business <span className="text-red-500">*</span>
				</label>
				<SearchableSelect id={id('company')} options={companyOptions} value={draft.companyId} onChange={(value) => set('companyId', value)} disabled={disabled || companiesLoading} placeholder={companiesLoading ? 'Loading…' : 'Search businesses'} hasError={Boolean(errors.companyId)} />
				{errors.companyId && <p className="mt-4px text-14px text-red-600">{errors.companyId}</p>}
			</div>

			{/* Contact — optional for CSR/JSR, required for a Full User. */}
			<div className="grid grid-cols-1 tablet:grid-cols-2 gap-12px">
				<Input id={id('email')} label={required ? 'Email' : 'Email (optional)'} type="email" required={required} value={draft.email} onChange={(e) => set('email', e.target.value)} error={errors.email} autoComplete="off" />
				<Input id={id('phone')} label={required ? 'Phone' : 'Phone (optional)'} type="tel" required={required} value={draft.phone} onChange={(e) => set('phone', e.target.value)} error={errors.phone} />
			</div>

			{/* Password — a Full User gets a friendly generated one they must
			    change on first sign-in. A CSR/JSR only needs one if they are
			    also given an email; with an email and no password the server
			    generates one and emails it. */}
			<div className="w-full">
				<Input
					id={id('password')}
					label={required ? 'Temporary password' : 'Password (optional)'}
					required={required}
					value={draft.password}
					onChange={(e) => set('password', e.target.value)}
					error={errors.password}
					autoComplete="new-password"
					helperText={required ? 'They must change it the first time they sign in.' : 'Only used if you add an email. Leave blank to have one generated and emailed.'}
				/>
				<LinkButton size="sm" onClick={() => set('password', generateTempPassword())}>
					Generate a password
				</LinkButton>
			</div>

			{/* "Basic information" a Full User walks away with. */}
			{required && (
				<Alert variant="info">
					{`${draft.firstName.trim() || 'They'} will start as a Technician — they can sign in, see the jobs assigned to them, and record their work. We'll email a welcome message with a sign-in link and this temporary password. You can change their role later in Admin → Users.`}
				</Alert>
			)}
		</fieldset>
	);
};

export default QuickAddPersonFieldset;
